import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import jsPDF from "jspdf";
import "jspdf-autotable";
import "./income.css";

const normalizeType = (t) => {
  const x = String(t || "")
    .trim()
    .toLowerCase();

  if (x === "revenue" || x === "income") return "Revenue";

  // handle common variants you might have in CoA
  if (x === "cost of sales" || x === "cost of goods sold" || x === "cogs")
    return "Cost of Sales";

  if (x === "expense" || x === "expenses" || x === "operating expense")
    return "Expense";

  return "Unknown";
};

// Revenue positive when credit > debit
// COS/Expense positive when debit > credit
// ✅ Golden rule mapping for this app:
// money_in  = DEBIT (receiver)
// money_out = CREDIT (giver)
const amountForPL = (section, debit, credit) => {
  const dr = Number(debit) || 0;
  const cr = Number(credit) || 0;
  if (section === "Revenue") return cr - dr;
  if (section === "Cost of Sales") return dr - cr;
  if (section === "Expense") return dr - cr;
  return 0;
};

const ProfitLoss = () => {
  const { companyId } = useCompany();

  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [incomeData, setIncomeData] = useState({
    revenue: [],
    cos: [],
    expense: [],
    totals: { revenue: 0, cos: 0, expense: 0, gross: 0, net: 0 },
  });

  const [expanded, setExpanded] = useState({
    Revenue: false,
    "Cost of Sales": false,
    Expense: false,
  });

  // Initialize date range to last 12 months
  useEffect(() => {
    const today = new Date();
    const start = new Date();
    start.setMonth(today.getMonth() - 12);

    setFilterStartDate(start.toISOString().slice(0, 10));
    setFilterEndDate(today.toISOString().slice(0, 10));
  }, []);

  const toggle = (section) =>
    setExpanded((e) => ({ ...e, [section]: !e[section] }));

  const fetchProfitLoss = useCallback(async () => {
    if (!companyId || !filterStartDate || !filterEndDate) return;

    setLoading(true);
    setErr("");

    try {
      /**
       * ✅ SQL aggregation approach (fixes Supabase 1000-row pagination cap)
       * - Use RPC: public.get_pl_account_sums(company_id, start_date, end_date)
       * - Returns totals per account (debit/credit) for Revenue/COS/Expense
       */
      const { data: rows, error } = await supabase.rpc("get_pl_account_sums", {
        p_company_id: companyId,
        p_start_date: filterStartDate,
        p_end_date: filterEndDate,
      });

      if (error) throw error;

      const safeRows = rows || [];

      // Keep this for compatibility / future-proofing (in case account_type comes back messy)
      const items = safeRows.map((r) => ({
        id: r.account_id,
        name: r.account_name || "Unknown",
        type: normalizeType(r.account_type),
        debit: Number(r.total_debit) || 0, // ✅ money_in aggregated as debit
        credit: Number(r.total_credit) || 0, // ✅ money_out aggregated as credit
      }));

      const buildSection = (section) =>
        items
          .filter((a) => a.type === section)
          .map((a) => {
            const amt = amountForPL(section, a.debit, a.credit);
            return { description: a.name, amount: amt };
          })
          .filter((x) => Math.abs(Number(x.amount) || 0) > 0.0001)
          .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));

      const revenueItems = buildSection("Revenue");
      const cosItems = buildSection("Cost of Sales");
      const expenseItems = buildSection("Expense");

      const sum = (arr) => arr.reduce((s, x) => s + (Number(x.amount) || 0), 0);

      const totals = {
        revenue: sum(revenueItems),
        cos: sum(cosItems),
        expense: sum(expenseItems),
      };
      totals.gross = totals.revenue - totals.cos;
      totals.net = totals.gross - totals.expense;

      setIncomeData({
        revenue: revenueItems,
        cos: cosItems,
        expense: expenseItems,
        totals,
      });

      // Helpful debug (remove later)
      if (
        safeRows.length > 0 &&
        revenueItems.length + cosItems.length + expenseItems.length === 0
      ) {
        console.warn(
          "Income.js: PL rows returned but no P&L items matched. Check CoA types (must be Revenue / Cost of Sales / Expense).",
          { sampleRows: safeRows.slice(0, 10) }
        );
      }
    } catch (e) {
      console.error("Income Statement fetch error:", e);
      setErr("Failed to compute income statement.");
    } finally {
      setLoading(false);
    }
  }, [companyId, filterStartDate, filterEndDate]);

  useEffect(() => {
    fetchProfitLoss();
  }, [fetchProfitLoss]);

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text(
      `Income Statement (${filterStartDate} – ${filterEndDate})`,
      10,
      12
    );

    const body = [
      ["Total Revenue", `₦${incomeData.totals.revenue.toLocaleString()}`],
      ...(expanded.Revenue
        ? incomeData.revenue.map((r) => [
            r.description,
            `₦${Number(r.amount || 0).toLocaleString()}`,
          ])
        : []),

      ["Cost of Sales", `₦${incomeData.totals.cos.toLocaleString()}`],
      ...(expanded["Cost of Sales"]
        ? incomeData.cos.map((c) => [
            c.description,
            `₦${Number(c.amount || 0).toLocaleString()}`,
          ])
        : []),

      ["Gross Profit", `₦${incomeData.totals.gross.toLocaleString()}`],

      ["Total Expenses", `₦${incomeData.totals.expense.toLocaleString()}`],
      ...(expanded.Expense
        ? incomeData.expense.map((e) => [
            e.description,
            `₦${Number(e.amount || 0).toLocaleString()}`,
          ])
        : []),

      [
        incomeData.totals.net >= 0 ? "Net Profit" : "Net Loss",
        `₦${incomeData.totals.net.toLocaleString()}`,
      ],
    ];

    doc.autoTable({
      head: [["Description", "Amount (₦)"]],
      body,
      startY: 18,
      theme: "striped",
    });
    doc.save("income_statement.pdf");
  };

  return (
    <div className="profit-loss-container">
      <div className="pl-header">
        <div>
          <h2 className="pl-title">
            Income Statement: {filterStartDate} – {filterEndDate}
          </h2>
          <p className="pl-sub">Company-wide · Journal-based reporting</p>
        </div>
        <button onClick={exportToPDF} id="export-btn">
          Export to PDF
        </button>
      </div>

      <div className="filter-card">
        <div className="filter-row">
          <div className="input-group">
            <label>Start Date</label>
            <input
              type="date"
              value={filterStartDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
            />
          </div>
          <div className="input-group">
            <label>End Date</label>
            <input
              type="date"
              value={filterEndDate}
              onChange={(e) => setFilterEndDate(e.target.value)}
            />
          </div>
          <div className="input-group">
            <label>&nbsp;</label>
            <button onClick={fetchProfitLoss} className="apply-btn">
              Apply Filter
            </button>
          </div>
        </div>
        {err && <p className="error-msg">{err}</p>}
      </div>

      <div className="income-card">
        {loading ? (
          <div className="loading">Loading…</div>
        ) : (
          <table className="income-table">
            <tbody>
              {/* Revenue */}
              <tr className="group-header" onClick={() => toggle("Revenue")}>
                <td>Total Revenue</td>
                <td className="amt">
                  ₦{incomeData.totals.revenue.toLocaleString()}
                </td>
                <td className="toggle-icon">{expanded.Revenue ? "▾" : "▸"}</td>
              </tr>
              {expanded.Revenue &&
                incomeData.revenue.map((r, i) => (
                  <tr key={`rev-${i}`} className="breakdown">
                    <td>{r.description}</td>
                    <td className="amt">
                      ₦{Number(r.amount || 0).toLocaleString()}
                    </td>
                    <td />
                  </tr>
                ))}

              {/* Cost of Sales */}
              <tr
                className="group-header"
                onClick={() => toggle("Cost of Sales")}
              >
                <td>Cost of Sales</td>
                <td className="amt">
                  ₦{incomeData.totals.cos.toLocaleString()}
                </td>
                <td className="toggle-icon">
                  {expanded["Cost of Sales"] ? "▾" : "▸"}
                </td>
              </tr>
              {expanded["Cost of Sales"] &&
                incomeData.cos.map((c, i) => (
                  <tr key={`cos-${i}`} className="breakdown">
                    <td>{c.description}</td>
                    <td className="amt">
                      ₦{Number(c.amount || 0).toLocaleString()}
                    </td>
                    <td />
                  </tr>
                ))}

              {/* Gross Profit */}
              <tr className="group-total">
                <td>Gross Profit</td>
                <td className="amt">
                  ₦{incomeData.totals.gross.toLocaleString()}
                </td>
                <td />
              </tr>

              {/* Expenses */}
              <tr className="group-header" onClick={() => toggle("Expense")}>
                <td>Total Expenses</td>
                <td className="amt">
                  ₦{incomeData.totals.expense.toLocaleString()}
                </td>
                <td className="toggle-icon">{expanded.Expense ? "▾" : "▸"}</td>
              </tr>
              {expanded.Expense &&
                incomeData.expense.map((e, i) => (
                  <tr key={`exp-${i}`} className="breakdown">
                    <td>{e.description}</td>
                    <td className="amt">
                      ₦{Number(e.amount || 0).toLocaleString()}
                    </td>
                    <td />
                  </tr>
                ))}

              {/* Net */}
              <tr className="group-total final">
                <td>
                  {incomeData.totals.net >= 0 ? "Net Profit" : "Net Loss"}
                </td>
                <td className="amt">
                  ₦{incomeData.totals.net.toLocaleString()}
                </td>
                <td />
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ProfitLoss;
